<script setup lang="ts">
import { MeasureResult } from '@Shared/utils/types';
import { MetricType, DeltaType, calculateDeltaValue } from './utility';
import DriverGauge from './parts/DriverGauge.vue';
import SkeletonDriverGauge from './skeletons/parts/DriverGauge.vue';

const props = defineProps<{
  title: string;
  isFrontline: boolean;
  inPopup: boolean;
  measureResults: MeasureResult[];
  metricType: MetricType; // dont think this will be used
}>();

const emit = defineEmits<{
  (event: 'showDetails'): void;
}>();

// Used to get strongest and weakest drivers
// For us to show drivers we want to have a current value
const driversOrderedAscendingByValue = computed(() =>
  [...props.measureResults]
    .filter((driver) => driver.currentValue != null)
    .sort((a, b) => (a.currentValue ?? 0) - (b.currentValue ?? 0))
);

const filteredDrivers = computed(() => {
  return [...props.measureResults].filter((driver) => {
    return (
      driver.currentValue != null &&
      driver != losingStrengthDriver.value &&
      driver != weakestDriver.value &&
      driver != strongestDriver.value
    );
  });
});

// Used for losing strength
// We need both current & previous since we want delta to exist
const driversOrderedAscendingByDelta = computed(() =>
  [...props.measureResults]
    .filter((driver) => driver.currentValue != null && driver.previousValue != null)
    .sort(
      (a, b) =>
        (calculateDeltaValue(a, DeltaType.PointDelta) ?? 0) - (calculateDeltaValue(b, DeltaType.PointDelta) ?? 0)
    )
);

const strongestDriver = computed(() => {
  if (driversOrderedAscendingByValue.value.length > 0) {
    return driversOrderedAscendingByValue.value[driversOrderedAscendingByValue.value.length - 1];
  } else {
    return null;
  }
});

const weakestDriver = computed(() => {
  if (driversOrderedAscendingByValue.value.length > 0) {
    return driversOrderedAscendingByValue.value[0];
  } else {
    return null;
  }
});

const losingStrengthDriver = computed(() => {
  if (
    driversOrderedAscendingByDelta.value.length > 0 &&
    (driversOrderedAscendingByDelta.value[0].currentValue ?? 0) <
      (driversOrderedAscendingByDelta.value[0].previousValue ?? 0)
  ) {
    return driversOrderedAscendingByDelta.value[0];
  } else {
    return null;
  }
});
</script>

<template>
  <div
    class="flex sm:flex-row h-full flex-wrap xs:w-full lg:w-auto"
    :class="{
      'bg-[#F9FAFB]': !isFrontline && !inPopup,
    }"
    id="DriverGauges"
  >
    <div
      class="flex flex-col h-full rounded-[8px] w-full"
      :class="{
        'bg-[#F9FAFB] pb-4 2xl:pb-0': !isFrontline && !inPopup,
      }"
    >
      <div
        class="flex items-center font-bold xl:text-4 2xl:text-5 3xl:text-10 3xl:mt-8 xs:text-5 font-family[Poppins] text-[#4F4F4F]"
        :class="{
          'ml-0 pt-5 pb-5': isFrontline,
          'ml-4 2xl:ml-6 3xl:ml-10 mb-2 mt-2 mr-4 2xl:mt-4 2xl:mb-10 3xl:mt-8 3xl:mb-17': !isFrontline && !inPopup,
          'h-min': isFrontline && inPopup,
        }"
      >
        <div class="inline-block" v-if="!inPopup">
          {{ title }}
        </div>
        <a
          id="show-driver-modal-button"
          v-if="isFrontline && !inPopup"
          @click="emit('showDetails')"
          class="text-3 mr-4 color-[#C73362] font-400 font-[Roboto] underline ml-auto lg:inline xs:hidden cursor-pointer"
        >
          View More
        </a>
        <a
          v-if="isFrontline && !inPopup"
          id="show-driver-modal-button-mobile"
          @click="emit('showDetails')"
          class="lg:hidden inline ml-auto i-mdi-chevron-right bg-[#4F4F4F] cursor-pointer"
          style="width: 2rem; height: 2rem"
        >
        </a>
      </div>
      <div
        class="flex flex-col sm:h-full min-h-0"
        :class="[
          // we want the height to not overfill the container, sadly this is a lil tricky to do
          // with flexboxes & svgs, simplest is just to force it to fit by using the min-height: 0
          {
            'flex-col': inPopup,
          },
        ]"
      >
        <!-- existing DriverGauge components -->
        <div class="flex lg:flex-row xs:flex-col items-center w-full">
          <DriverGauge
            id="strongest-driver"
            color="#0C8A65"
            title="STRONGEST"
            v-if="strongestDriver"
            :driver="strongestDriver"
            :metricType="metricType"
            :inPopup="false"
            :usePurpleGauge="false"
            class="w-full sm:w-1/3 xs:w-1/2"
          />
          <SkeletonDriverGauge
            disableAnimation
            title="STRONGEST"
            v-else
            class="w-full sm:w-1/3 xs:w-1/2"
            :is-popup="props.isFrontline"
          />
          <DriverGauge
            id="losing-strength-driver"
            color="#D49AA6"
            title="LOSING STRENGTH"
            v-if="losingStrengthDriver"
            :driver="losingStrengthDriver"
            :metricType="metricType"
            :inPopup="false"
            :usePurpleGauge="false"
            class="w-full sm:w-1/3 xs:w-1/2"
          />
          <SkeletonDriverGauge
            disableAnimation
            title="LOSING STRENGTH"
            v-else
            class="w-full sm:w-1/3 xs:w-1/2"
            :is-popup="props.isFrontline"
          />
          <DriverGauge
            id="weakest-driver"
            color="#B20125"
            title="WEAKEST"
            v-if="weakestDriver"
            :driver="weakestDriver"
            :metricType="metricType"
            :inPopup="false"
            :usePurpleGauge="false"
            class="w-full sm:w-1/3 xs:w-1/2"
          />
          <SkeletonDriverGauge
            disableAnimation
            title="WEAKEST"
            v-else
            class="w-full sm:w-1/3 xs:w-1/2"
            :is-popup="props.isFrontline"
          />
        </div>
        <!-- Other DriverGauge components -->
        <div
          class="flex flex-wrap w-full justify-center lg:justify-start"
          v-if="inPopup"
          :class="{
            // TODO: this padding is added for the CX Muscles pop-up only for now. The height calculation is weird and requires more investigation.
            'xs:pb-100px': isFrontline && inPopup,
            'lg:pb-0': isFrontline && inPopup,
          }"
          style="border-top: 1px solid #e0e0e0; padding-top: 20px"
        >
          <DriverGauge
            :id="`other-driver-${driver.label}`"
            v-for="driver in filteredDrivers"
            :key="driver.label"
            color="#621A4B"
            title=""
            :driver="driver"
            :metricType="metricType"
            :inPopup="true"
            :usePurpleGauge="true"
            class="sm:w-1/3 xs:w-1/2 h-auto"
          />
        </div>
      </div>
    </div>
  </div>
</template>
